import { zodResolver } from "@hookform/resolvers/zod";
import { MailCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { authApi } from "@/client/features/auth/auth-api";
import { ApiError } from "@/client/lib/api";

const schema = z.object({ email: z.string().email("Enter a valid email") });
type Values = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: "" } });
  const submitted = form.formState.isSubmitSuccessful;

  const onSubmit = async (values: Values) => {
    try {
      await authApi.forgotPassword(values);
    } catch (err) {
      // Don't reveal whether an account exists. Only surface unexpected
      // (server/network) failures; treat 4xx as a normal "sent" outcome.
      if (!(err instanceof ApiError) || err.status >= 500) {
        toast.error("Something went wrong. Please try again.");
        throw err;
      }
    }
  };

  if (submitted) {
    return (
      <Card>
        <CardHeader>
          <div className="bg-muted mb-1 flex size-10 items-center justify-center rounded-full">
            <MailCheck className="size-5" />
          </div>
          <CardTitle>Check your email</CardTitle>
          <CardDescription>
            If an account exists for that address, we've sent a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reset password</CardTitle>
        <CardDescription>Enter your email and we'll send you a reset link.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="justify-center">
        <Link to="/login" className="text-muted-foreground hover:text-foreground text-sm">
          Back to sign in
        </Link>
      </CardFooter>
    </Card>
  );
}
